/**
 * voice-v8 — Theme + Chat-Style Application.
 *
 * 2026-07-06 (Martin, ZWEI-ACHSEN-REDESIGN): Helligkeit und Farbe sind jetzt
 * ORTHOGONAL. Vorher war jedes Farb-Theme ein kompletter Look MIT fest
 * eingebauter Helligkeit (Wald/Ozean immer dunkel, Sand immer hell) — genau
 * DAS war Martins Bug: "Hell + Wald" sprang zurück auf dunkel, weil Wald
 * dunkel *war*. Jetzt:
 *   Achse 1  Brightness  auto | light | dark  → data-mode auf <html>
 *   Achse 2  Palette     salbei | wald | ozean | sand → data-palette auf <html>
 * Jede Palette existiert in BEIDER Helligkeit (CSS-Matrix). "salbei" ist die
 * neutrale Default-Palette und verhält sich exakt wie früher (kein data-palette;
 * Auto = data-mode weg → @media regelt). Für die Farb-Paletten löst dieses
 * Modul "Automatisch" in eine KONKRETE Helligkeit auf, damit der
 * [data-palette][data-mode]-Selektor greift.
 *
 * Single source of truth: appearance.ts ist der EINZIGE Theme-Writer und der
 * EINZIGE Server-Adopter.
 *
 * 23.09.2026, Ticket 2511: Der Weg zum Server ist derselbe wie für jede
 * andere Einstellung — vormerken, senden, Bestätigung abwarten, bei Fehler
 * sichtbar melden und nachreichen (einstellungen-abgleich.ts). Vorher
 * schickte jeder Setter einen eigenen PUT und verwarf jeden Fehler still.
 * Serverwerte kommen als Ereignis `voice-v8:darstellung-vom-server` herein;
 * der Abgleich schickt sie nur für Felder, an denen hier gerade nichts
 * unterwegs ist — das ersetzt die frühere Sperre `userPinned`, die nach dem
 * ersten Klick JEDE spätere Übernahme für die ganze Sitzung abwies.
 */

import { pushAlltagWidgetInfo } from "./native-recorder.js";
import { merkeAenderungen, sendeVorgemerktes } from "./einstellungen-ausstehend.js";

/**
 * Einen Darstellungswert vormerken und an den Server schicken. Wie es
 * ausging, meldet der Abgleich (`voice-v8:settings-sync`); ohne Netz bleibt
 * es vorgemerkt und wird nachgereicht.
 */
function schickeAnServer(werte: Record<string, string>): void {
  void sendeVorgemerktes(merkeAenderungen(werte));
}

// --- Legacy-Kompatibilität (alte kombinierte Werte) -------------------------
const THEME_MODES = [
  "auto",
  "light",
  "dark",
  "wald",
  "ozean",
  "sand",
] as const;
type ThemeMode = (typeof THEME_MODES)[number];
// --- Achse 1: Helligkeit ----------------------------------------------------
export const BRIGHTNESSES = ["auto", "light", "dark"] as const;
type Brightness = (typeof BRIGHTNESSES)[number];
export const BRIGHTNESS_LABELS: Record<Brightness, string> = {
  auto: "Automatisch",
  light: "Hell",
  dark: "Dunkel",
};

// --- Achse 2: Farb-Palette --------------------------------------------------
export const PALETTES = ["salbei", "wald", "ozean", "sand"] as const;
export type Palette = (typeof PALETTES)[number];
export const PALETTE_LABELS: Record<Palette, string> = {
  salbei: "Salbei",
  wald: "Wald",
  ozean: "Ozean",
  sand: "Sand",
};

type ChatStyle = "bubbles" | "transcript";

/**
 * Wie Zuhause aussieht (2026-08-29).
 *
 * Martin hat aus drei Entwuerfen zwei ausgesucht und wollte sie umschaltbar:
 * *„Ich finde das erste und das dritte am besten. Geil wäre natürlich, wenn
 * man dann in Einstellungen hin und her schalten könnte."*
 *
 *   blatt   — ruhiges Papier: gezeichnete Tagesszene, Haarlinien statt Kaesten
 *   kacheln — Kacheln, jede Sache mit eigenem Bild
 *
 * ⚠️ Es ist bewusst dieselbe Achse wie Helligkeit und Palette: EIN Ort, an dem
 * das Aussehen entschieden wird. Ein eigener Mechanismus daneben waere der
 * dritte, und der laeuft irgendwann auseinander.
 */
export const ZUHAUSE_STILE = ["blatt", "kacheln"] as const;
type ZuhauseStil = (typeof ZUHAUSE_STILE)[number];
export const ZUHAUSE_STIL_LABELS: Record<ZuhauseStil, string> = {
  blatt: "Blatt",
  kacheln: "Kacheln",
};
const ZUHAUSE_STIL_KEY = "voice-v8.zuhause-stil";

function istZuhauseStil(v: unknown): v is ZuhauseStil {
  return typeof v === "string" && (ZUHAUSE_STILE as readonly string[]).includes(v);
}

export function getZuhauseStil(): ZuhauseStil {
  try {
    const v = localStorage.getItem(ZUHAUSE_STIL_KEY);
    if (istZuhauseStil(v)) return v;
  } catch {
    /* ignore */
  }
  return "blatt";
}

/** Schreibt den Stil an das <body> — von dort holt ihn das Blatt. */
export function applyZuhauseStil(stil: ZuhauseStil = getZuhauseStil()): void {
  document.body.dataset.zuhauseStil = stil;
}

export function setZuhauseStil(stil: ZuhauseStil): void {
  try {
    localStorage.setItem(ZUHAUSE_STIL_KEY, stil);
  } catch {
    /* ignore */
  }
  applyZuhauseStil(stil);
  // Damit der Stil auch auf dem naechsten Geraet gilt.
  schickeAnServer({ zuhauseStil: stil });
}

/**
 * Den Stil eines anderen Geräts übernehmen (über den Abgleich, Ticket 2511).
 * Gibt zurück, ob sich etwas geändert hat.
 */
export function adoptServerZuhauseStil(stil: unknown): boolean {
  if (!istZuhauseStil(stil) || stil === getZuhauseStil()) return false;
  try {
    localStorage.setItem(ZUHAUSE_STIL_KEY, stil);
  } catch {
    /* ignore */
  }
  applyZuhauseStil(stil);
  return true;
}

const THEME_KEY = "voice-v8.theme"; // hält Brightness (Legacy: evtl. kombiniert)
const PALETTE_KEY = "voice-v8.palette"; // hält Palette

/** Alte Einzelwerte → {Helligkeit, Palette}. Für Migration von localStorage
 *  und alten Server-Ständen (themeMode="wald" o.ä.). */
const LEGACY: Record<string, { b: Brightness; p: Palette }> = {
  auto: { b: "auto", p: "salbei" },
  light: { b: "light", p: "salbei" },
  dark: { b: "dark", p: "salbei" },
  wald: { b: "dark", p: "wald" },
  ozean: { b: "dark", p: "ozean" },
  sand: { b: "light", p: "sand" },
};

function isBrightness(v: unknown): v is Brightness {
  return v === "auto" || v === "light" || v === "dark";
}
function isPalette(v: unknown): v is Palette {
  return typeof v === "string" && (PALETTES as readonly string[]).includes(v);
}

export function getBrightness(): Brightness {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (isBrightness(v)) return v;
    if (typeof v === "string" && LEGACY[v]) return LEGACY[v].b; // altes Kombi
  } catch {
    /* localStorage unavailable */
  }
  return "auto";
}

export function getPalette(): Palette {
  try {
    const v = localStorage.getItem(PALETTE_KEY);
    if (isPalette(v)) return v;
    const t = localStorage.getItem(THEME_KEY); // aus altem Kombi-Wert ableiten
    if (typeof t === "string" && LEGACY[t]) return LEGACY[t].p;
  } catch {
    /* ignore */
  }
  return "salbei";
}

/** Kompatibilitäts-Getter: kombinierter Wert (Palette dominiert, sonst
 *  Helligkeit) — nur noch für Alt-Aufrufer / Toggle-Titel. */
export function getTheme(): ThemeMode {
  const p = getPalette();
  if (p !== "salbei") return p;
  return getBrightness();
}

/** Der EINZIGE Schreibpfad auf <html>. Löst Auto für Paletten konkret auf. */
function applyResolved(ueber?: Brightness): void {
  const root = document.documentElement;
  const brightness = ueber ?? getBrightness();
  const palette = getPalette();
  const osLight = matchMedia("(prefers-color-scheme: light)").matches;
  const resolved: "light" | "dark" =
    brightness === "auto" ? (osLight ? "light" : "dark") : brightness;

  if (palette === "salbei") {
    // Neutral = exakt wie früher: kein data-palette; Auto → @media regelt.
    delete root.dataset.palette;
    if (brightness === "auto") delete root.dataset.mode;
    else root.dataset.mode = brightness;
  } else {
    // Farb-Palette: data-mode IMMER konkret, damit
    // :root[data-palette=…][data-mode=…] greift (auch bei Auto).
    root.dataset.palette = palette;
    root.dataset.mode = resolved;
  }
  updateThemeToggleUi(getBrightness());
  // Nach dem CSS-Wechsel eine Frame warten, damit die Sonde
  // --paper/--ember der NEUEN Palette liest (Wald gruen, nicht altes Orange).
  requestAnimationFrame(() => {
    void pushAlltagWidgetInfo({
      uiIsDark: resolved === "dark",
      uiPalette: palette,
    });
  });
}

/** Kompat-Signatur (Alt-Aufrufer geben ThemeMode) — Argument wird ignoriert,
 *  der Zustand kommt aus localStorage. */
export function applyTheme(_mode?: ThemeMode): void {
  applyResolved();
}

/** Nur für die Testseite (Zuruf „dunkel“): zeigt eine Helligkeit, ohne sie zu speichern und ohne den Server zu fragen. */
export function applyBrightnessHere(b: Brightness | null): void {
  applyResolved(b ?? undefined);
}

/** Helligkeit setzen (Achse 1) — sofort sichtbar, Save fire-and-forget. */
export function setBrightness(b: Brightness): void {
  try {
    localStorage.setItem(THEME_KEY, b);
  } catch {
    /* ignore */
  }
  applyResolved();
  schickeAnServer({ themeMode: b });
}

/** Farb-Palette setzen (Achse 2). */
export function setPalette(p: Palette): void {
  try {
    localStorage.setItem(PALETTE_KEY, p);
  } catch {
    /* ignore */
  }
  applyResolved();
  schickeAnServer({ themePalette: p });
}

/**
 * Server-Werte übernehmen. Eine frische Wahl auf diesem Gerät schützt der
 * Abgleich: er schickt nur Felder, an denen hier nichts unterwegs ist.
 * Gibt zurück, ob sich etwas geändert hat.
 */
function adoptServerTheme(mode?: unknown, palette?: unknown): boolean {
  let changed = false;
  if (isBrightness(mode)) {
    if (mode !== getBrightness()) {
      try {
        localStorage.setItem(THEME_KEY, mode);
      } catch {
        /* ignore */
      }
      changed = true;
    }
  } else if (typeof mode === "string" && LEGACY[mode]) {
    // Alter Server liefert kombinierten Wert (z.B. "wald") → aufsplitten.
    const m = LEGACY[mode];
    try {
      localStorage.setItem(THEME_KEY, m.b);
      if (!isPalette(palette)) localStorage.setItem(PALETTE_KEY, m.p);
    } catch {
      /* ignore */
    }
    changed = true;
  }
  if (isPalette(palette) && palette !== getPalette()) {
    try {
      localStorage.setItem(PALETTE_KEY, palette);
    } catch {
      /* ignore */
    }
    changed = true;
  }
  if (changed) applyResolved();
  return changed;
}

/** Was man gerade SIEHT — „Automatisch" aufgelöst nach dem System. */
function sichtbareHelligkeit(): "light" | "dark" {
  const b = getBrightness();
  if (b !== "auto") return b;
  return matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

/**
 * Mond-Klick: zwischen Hell und Dunkel wechseln — gemessen an dem, was man
 * SIEHT. Palette bleibt, der Wert wird server-persistiert.
 *
 * ⚠️ 23.09.2026 — Martin: *„wenn ich auf den Mond klicke, muss ich 2 mal
 *    klicken, damit ich in den Darkmodus komme — zurück geht dann bei
 *    einmal klicken."*
 *
 *    Hier stand eine Runde „Automatisch → Hell → Dunkel". Bei hellem System
 *    sieht „Automatisch" schon hell aus: der erste Klick schaltete auf
 *    „Hell", und sichtbar geschah NICHTS. Ein Knopf, der beim ersten Druck
 *    nichts tut, sieht kaputt aus. „Automatisch" bleibt in den
 *    Einstellungen wählbar (Modus) — der Mond ist der schnelle Wechsel.
 */
export function cycleTheme(): void {
  setBrightness(sichtbareHelligkeit() === "dark" ? "light" : "dark");
}

// 23.09.2026, Ticket 2511: Werte eines anderen Geräts kommen über den
// Abgleich (einstellungen-abgleich.ts) — geschrieben wird nur hier. Hat sich
// etwas geändert, sagt es dasselbe Ereignis wie jede andere Übernahme vom
// Server: Eine offene Einstellungsseite zeichnet dann ihre Knöpfe neu.
try {
  window.addEventListener("voice-v8:darstellung-vom-server", (ev) => {
    const d = (ev as CustomEvent<Record<string, unknown>>).detail ?? {};
    const felder: string[] = [];
    if (("themeMode" in d || "themePalette" in d) && adoptServerTheme(d.themeMode, d.themePalette)) {
      felder.push("themeMode", "themePalette");
    }
    if ("zuhauseStil" in d && adoptServerZuhauseStil(d.zuhauseStil)) felder.push("zuhauseStil");
    if (felder.length === 0) return;
    window.dispatchEvent(
      new CustomEvent("voice-v8:settings-changed", { detail: { quelle: "server", felder } }),
    );
  });
} catch {
  /* ohne Fenster */
}

// Im Auto-Modus auf OS-Hell/Dunkel-Wechsel reagieren — sonst bliebe der zur
// Ladezeit aufgelöste Look (bzw. bei Paletten die konkrete Helligkeit) kleben.
try {
  matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
    if (getBrightness() === "auto") applyResolved();
  });
} catch {
  /* ältere Browser ohne addEventListener auf MediaQueryList — egal */
}

export function getChatStyle(): ChatStyle {
  // 2026-06-29 (Martin): Chat-Stil ist auf Bubbles fixiert.
  return "bubbles";
}

export function applyChatStyle(_style: ChatStyle): void {
  const chat = document.getElementById("chat");
  if (chat) chat.dataset.chatStyle = "bubbles";
}

function updateThemeToggleUi(brightness: Brightness): void {
  const btn = document.getElementById("theme-toggle");
  if (!btn) return;
  btn.dataset.theme = brightness;
  const pal = getPalette();
  const palTxt = pal === "salbei" ? "" : ` · ${PALETTE_LABELS[pal]}`;
  btn.title = `Helligkeit: ${BRIGHTNESS_LABELS[brightness]}${palTxt} — klicken zum Wechseln`;
  btn.setAttribute("aria-label", btn.title);
  btn.querySelectorAll<HTMLElement>("svg").forEach((svg) => {
    svg.hidden = !svg.classList.contains(`theme-icon-${brightness}`);
  });
}
